import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';

function ProductFormPopupEdit({ widget, onUpdate, colors }) {
    const [editData, setEditData] = useState({ ...widget });
    const [formFields, setFormFields] = useState(widget.formFields || [
        { name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: '' },
        { name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: '' }
    ]);

    useEffect(() => {
        setEditData({ ...widget });
        setFormFields(widget.formFields || [
            { name: 'name', label: 'Full Name', type: 'text', required: true, placeholder: '' },
            { name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: '' }
        ]);
    }, [widget]);

    const handleInputChange = (e) => {
        const { name, type, value, checked } = e.target;
        const updatedValue = type === 'checkbox' ? checked : value;
        
        const updatedEditData = { 
            ...editData, 
            [name]: updatedValue,
            formFields: formFields
        };
        
        setEditData(updatedEditData);
        onUpdate(updatedEditData);
    };

    const handleFormFieldChange = (index, field, value) => {
        const updatedFields = [...formFields];
        updatedFields[index] = { ...updatedFields[index], [field]: value };
        setFormFields(updatedFields);
        
        const updatedEditData = { 
            ...editData, 
            formFields: updatedFields 
        };
        setEditData(updatedEditData);
        onUpdate(updatedEditData);
    };

    const addFormField = () => {
        const newField = {
            name: `field_${Date.now()}`,
            label: 'New Field',
            type: 'text',
            required: false,
            placeholder: ''
        };
        const updatedFields = [...formFields, newField];
        setFormFields(updatedFields);
        
        const updatedEditData = { 
            ...editData, 
            formFields: updatedFields 
        };
        setEditData(updatedEditData);
        onUpdate(updatedEditData);
    };

    const removeFormField = (index) => {
        const updatedFields = formFields.filter((_, i) => i !== index);
        setFormFields(updatedFields);
        
        const updatedEditData = { 
            ...editData, 
            formFields: updatedFields 
        };
        setEditData(updatedEditData);
        onUpdate(updatedEditData);
    };

    const fieldTypeOptions = [
        { value: 'text', label: 'Text' },
        { value: 'email', label: 'Email' },
        { value: 'tel', label: 'Phone' },
        { value: 'textarea', label: 'Textarea' },
        { value: 'select', label: 'Select' },
        { value: 'number', label: 'Number' },
        { value: 'url', label: 'URL' }
    ];

    return (
        <div className="widget-edit-container">
            <h3>Product Form Popup Settings</h3>
            
            {/* Basic Settings */}
            <div className="edit-section">
                <h4>Basic Settings</h4>
                
                <div className="form-group">
                    <label>Widget Title:</label>
                    <input
                        type="text"
                        name="title"
                        value={editData.title || ''}
                        onChange={handleInputChange}
                        placeholder="Product Form Popup"
                    />
                </div>

                <div className="form-group">
                    <label>Description:</label>
                    <textarea
                        name="description"
                        value={editData.description || ''}
                        onChange={handleInputChange}
                        placeholder="Collect user information for free digital products"
                        rows="3"
                    />
                </div>

                <div className="form-group">
                    <label>Product Title (shown in popup):</label>
                    <input
                        type="text"
                        name="productTitle"
                        value={editData.productTitle || ''}
                        onChange={handleInputChange}
                        placeholder="Sample Digital Product"
                    />
                </div>
            </div>

            {/* Styling Settings */}
            <div className="edit-section">
                <h4>Styling</h4>
                
                <div className="form-group">
                    <label>Background Color:</label>
                    <input
                        type="color"
                        name="backgroundColor"
                        value={editData.backgroundColor || '#ffffff'}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="form-group">
                    <label>Title Color:</label>
                    <input
                        type="color"
                        name="titleColor"
                        value={editData.titleColor || '#1f2937'}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="form-group">
                    <label>Title Size:</label>
                    <input
                        type="number"
                        name="titleSize"
                        value={parseInt(editData.titleSize) || 18}
                        onChange={handleInputChange}
                        min="12"
                        max="48"
                    />
                </div>

                <div className="form-group">
                    <label>Description Color:</label>
                    <input
                        type="color"
                        name="descriptionColor"
                        value={editData.descriptionColor || '#6b7280'}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="form-group">
                    <label>Description Size:</label>
                    <input
                        type="number"
                        name="descriptionSize"
                        value={parseInt(editData.descriptionSize) || 14}
                        onChange={handleInputChange}
                        min="10"
                        max="24"
                    />
                </div>

                <div className="form-group">
                    <label>Button Color:</label>
                    <input
                        type="color"
                        name="buttonColor"
                        value={editData.buttonColor || '#3b82f6'}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="form-group">
                    <label>Button Text Color:</label>
                    <input
                        type="color"
                        name="buttonTextColor"
                        value={editData.buttonTextColor || '#ffffff'}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="form-group">
                    <label>Button Size:</label>
                    <input
                        type="number"
                        name="buttonSize"
                        value={parseInt(editData.buttonSize) || 14}
                        onChange={handleInputChange}
                        min="10"
                        max="20"
                    />
                </div>
            </div>

            {/* Spacing Settings */}
            <div className="edit-section">
                <h4>Spacing</h4>
                
                <div className="form-group">
                    <label>Margin Top:</label>
                    <input
                        type="number"
                        name="marginTop"
                        value={editData.marginTop || 0}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="form-group">
                    <label>Margin Bottom:</label>
                    <input
                        type="number"
                        name="marginBottom"
                        value={editData.marginBottom || 0}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="form-group">
                    <label>Border Radius:</label>
                    <input
                        type="number"
                        name="borderRadius"
                        value={editData.borderRadius || 0}
                        onChange={handleInputChange}
                        min="0"
                        max="50"
                    />
                </div>
            </div>

            {/* Form Fields Configuration */}
            <div className="edit-section">
                <h4>Form Fields</h4>
                
                {formFields.map((field, index) => (
                    <div key={index} className="form-field-config" style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px',
                        backgroundColor: '#f9fafb'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h5 style={{ margin: 0 }}>Field {index + 1}</h5>
                            {formFields.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeFormField(index)}
                                    style={{
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        width: '28px',
                                        height: '28px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#dc2626';
                                        e.target.style.transform = 'scale(1.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = '#ef4444';
                                        e.target.style.transform = 'scale(1)';
                                    }}
                                    title="Remove Field"
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label>Field Name:</label>
                            <input
                                type="text"
                                value={field.name}
                                onChange={(e) => handleFormFieldChange(index, 'name', e.target.value)}
                                placeholder="field_name"
                            />
                        </div>

                        <div className="form-group">
                            <label>Label:</label>
                            <input
                                type="text"
                                value={field.label}
                                onChange={(e) => handleFormFieldChange(index, 'label', e.target.value)}
                                placeholder="Field Label"
                            />
                        </div>

                        <div className="form-group">
                            <label>Type:</label>
                            <select
                                value={field.type}
                                onChange={(e) => handleFormFieldChange(index, 'type', e.target.value)}
                            >
                                {fieldTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Placeholder:</label>
                            <input
                                type="text"
                                value={field.placeholder || ''}
                                onChange={(e) => handleFormFieldChange(index, 'placeholder', e.target.value)}
                                placeholder="Field placeholder text"
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={field.required || false}
                                    onChange={(e) => handleFormFieldChange(index, 'required', e.target.checked)}
                                />
                                Required Field
                            </label>
                        </div>

                        {field.type === 'select' && (
                            <div className="form-group">
                                <label>Options (one per line):</label>
                                <textarea
                                    value={field.options ? field.options.join('\n') : ''}
                                    onChange={(e) => handleFormFieldChange(index, 'options', e.target.value.split('\n').filter(opt => opt.trim()))}
                                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                                    rows="4"
                                />
                            </div>
                        )}
                    </div>
                ))}
                
                <button
                    type="button"
                    onClick={addFormField}
                    style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        marginTop: '8px'
                    }}
                >
                    + Add Field
                </button>
            </div>

            {/* Border Settings */}
            <div className="edit-section">
                <h4>Border</h4>
                
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            name="border"
                            checked={editData.border || false}
                            onChange={handleInputChange}
                        />
                        Show Border
                    </label>
                </div>

                {editData.border && (
                    <div className="form-group">
                        <label>Border Color:</label>
                        <input
                            type="color"
                            name="borderColor"
                            value={editData.borderColor || '#e5e7eb'}
                            onChange={handleInputChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProductFormPopupEdit;
